import {
  IEvaluationAssessor,
  IEvaluationCultureReview,
  IEvaluationGoalReview,
  IEvaluationSkillReview,
  ISingleEvaluationPure,
  IEvaluationPure,
  IEvaluationState,
  SingleEvaluationStatus,
  EvaluationAssessmentType,
  RevealReasonType,
} from '../common/models/Evaluation'
import { ICompany, ICompanyMember } from '../common/models/Company'
import { ITermState, ITermStateMember } from '../common/models/Term'
import {
  isMemberBuddyOfAnotherMember,
  isMemberGenerallyLeadOfMember,
} from '../common/helpers/isLeadOf'
import { getAllowedContributors } from '../common/helpers/company.helper'
import { adaptBackendCompanyToFrontendCompany } from '../context/services/loadCompany.service'
import { CollectionRevealState } from '../common/models/PdfExport'
import { IStatCardData } from '../components/evaluations/MemberStatBlocks'

// The subset of an evaluation this helper needs: the per-section reveal reasons
// the backend stamps while filtering hidden data for the viewer.
interface IRevealReasonsCarrier {
  revealReasons?: Partial<Record<EvaluationAssessmentType, RevealReasonType>>
}

// Per-section reveal mode, driven by the server's revealReason — the authoritative
// "this section is hidden for this viewer" signal. A reason → pending (show the
// "why" copy) or empty (notAssessed). No reason → the section is visible: a section
// that was assessed but produced no aggregate is the openable 'noScore' card,
// otherwise it's fully 'revealed'. Shared by the collection page and the PDF builder
// so both gate results identically (never leaking hidden data).
export const getSectionRevealState = (
  section: EvaluationAssessmentType,
  evaluation: IRevealReasonsCarrier | undefined,
  sectionData?: IStatCardData
): CollectionRevealState => {
  const reason = evaluation?.revealReasons?.[section]
  if (reason) {
    return reason === RevealReasonType.notAssessed ? 'empty' : 'pending'
  }
  // No reveal reason but no aggregate score → an assessor submitted but no allowed
  // score-contributor did. Individual input exists, so show the openable "no overall
  // score" card rather than the dead-end empty state.
  if (
    sectionData?.hasResults === false &&
    (sectionData?.assessors || []).length === 0
  ) {
    return 'empty'
  }
  if (sectionData?.hasResults === false) return 'noScore'
  return 'revealed'
}

export const canSeeScore = (
  company: ICompany,
  scoredMember: ICompanyMember,
  viewerMember: ICompanyMember
): boolean => {
  const config = company.settings?.evaluations?.scoreVisibility
  if (!config) {
    return false
  }
  const isSelf = String(viewerMember._id) === String(scoredMember._id)
  if (isSelf) {
    return config.self
  }
  const isBuddy = isMemberBuddyOfAnotherMember(viewerMember, scoredMember)
  if (isBuddy) {
    return config.buddies
  }
  const isLead = isMemberGenerallyLeadOfMember(viewerMember, scoredMember)
  if (isLead) {
    return config.leads
  }

  return config.peers
}

export const getCultureScores = (
  company: ICompany
): Array<{ value: number; label: string }> => {
  return (
    company?.settings?.evaluations?.cultureScores
      .map((val) => {
        return { value: val.score, label: val.label }
      })
      .sort((a, b) => a.value - b.value) || []
  )
}

export const getSkillScores = (
  company: ICompany
): Array<{ value: number; label: string }> => {
  return (
    company?.settings?.evaluations?.skillScores
      .map((val) => {
        return { value: val.score, label: val.label }
      })
      .sort((a, b) => a.value - b.value) ||
    [] ||
    []
  )
}

export const getVisibleAssessors = (
  company: ICompany,
  scoredMember: ICompanyMember
): ICompanyMember[] => {
  const config = company.settings?.evaluations?.scoreVisibility
  if (!config) {
    return company.allMembers
  }
  return company.allMembers.flatMap((m) => {
    const isSelf = String(m._id) === String(scoredMember._id)
    if (!config.self && isSelf) {
      return []
    } else if (config.self && isSelf) {
      return m
    }
    const isBuddy = isMemberBuddyOfAnotherMember(m, scoredMember)
    if (!config.buddies && isBuddy) {
      return []
    } else if (config.buddies && isBuddy) {
      return m
    }
    const isLead = isMemberGenerallyLeadOfMember(m, scoredMember)
    if (!config.leads && isLead) {
      return []
    } else if (config.leads && isLead) {
      return m
    }

    if (config.peers) {
      return m
    }
    return []
  })
}

export const areAllScoresByContributorsSubmitted = (
  assessments: ISingleEvaluationPure[],
  assessors: IEvaluationAssessor[],
  allowedContributors: string[]
): { goals: boolean; skills: boolean; culture: boolean } => {
  if (
    assessments.every(
      (assessment) =>
        assessment.status === SingleEvaluationStatus.DONE ||
        assessment.status === SingleEvaluationStatus.NOT_COMPLETE_IN_TIME
    )
  ) {
    return { goals: true, skills: true, culture: true }
  } else {
    const contributorsAssessments = assessments.filter((as) =>
      allowedContributors.includes(as.senderId)
    )
    if (contributorsAssessments.length > 0) {
      const typeDone = (type: 'goals' | 'skills' | 'culture') =>
        assessors.some((a) => a[type])
          ? contributorsAssessments.every((assessment) => {
              const assessor = assessors.find(
                (a) => a.memberId === assessment.senderId
              )
              return assessment[`${type}Submitted`] || !assessor?.[type]
            })
          : true

      return {
        goals: typeDone('goals'),
        skills: typeDone('skills'),
        culture: typeDone('culture'),
      }
    }
    return { goals: true, skills: true, culture: true }
  }
}

/**
 * Combines goals and skills from multiple evaluations, grouping them by ID and consolidating their fields,
 * Returns arrays of combined goals and skills based on the provided flags.
 */

export const combineAssessments = (
  evaluations: ISingleEvaluationPure[],
  includeGoals: boolean,
  includeSkills?: boolean,
  includeCultures?: boolean
) => {
  const goalMap = new Map<
    string,
    {
      fields: Array<{
        field: IEvaluationGoalReview['fields']
        senderId: string
        dateSubmitted: Date
      }>
    }
  >()

  const skillMap = new Map<
    string,
    {
      fields: Array<{
        field: IEvaluationSkillReview['fields']
        senderId: string
        dateSubmitted: Date
      }>
      levelId?: string
    }
  >()

  const cultureMap = new Map<
    string,
    {
      fields: Array<{
        field: IEvaluationCultureReview
        senderId: string
        dateSubmitted: Date
      }>
    }
  >()

  evaluations.forEach((evaluation) => {
    if (includeGoals) {
      evaluation.goals.forEach((goal) => {
        const existingGoal = goalMap.get(goal.goalId)

        if (existingGoal) {
          existingGoal.fields.push({
            field: goal.fields,
            senderId: evaluation.senderId,
            dateSubmitted:
              evaluation.goalsSubmitted || evaluation.dateSubmitted,
          })
        } else {
          // Create new group
          goalMap.set(goal.goalId, {
            fields: [
              {
                field: goal.fields,
                senderId: evaluation.senderId,
                dateSubmitted:
                  evaluation.goalsSubmitted || evaluation.dateSubmitted,
              },
            ],
          })
        }
      })
    }

    if (includeSkills) {
      evaluation.skills.forEach((skill) => {
        const existingSkill = skillMap.get(skill.skillId)

        if (existingSkill) {
          existingSkill.fields.push({
            field: skill.fields,
            senderId: evaluation.senderId,
            dateSubmitted:
              evaluation.skillsSubmitted || evaluation.dateSubmitted,
          })
        } else {
          skillMap.set(skill.skillId, {
            fields: [
              {
                field: skill.fields,
                senderId: evaluation.senderId,
                dateSubmitted:
                  evaluation.skillsSubmitted || evaluation.dateSubmitted,
              },
            ],
            levelId: skill.levelId,
          })
        }
      })
    }

    if (includeCultures) {
      evaluation.culture.forEach((culture) => {
        const existingCulture = cultureMap.get(culture.habitId)

        if (existingCulture) {
          existingCulture.fields.push({
            field: culture,
            senderId: evaluation.senderId,
            dateSubmitted:
              evaluation.cultureSubmitted ?? evaluation.dateSubmitted,
          })
        } else {
          cultureMap.set(culture.habitId, {
            fields: [
              {
                field: culture,
                senderId: evaluation.senderId,
                dateSubmitted:
                  evaluation.cultureSubmitted ?? evaluation.dateSubmitted,
              },
            ],
          })
        }
      })
    }
  })

  const combinedGoals = includeGoals
    ? Array.from(goalMap.entries()).map(([goalId, { fields }]) => ({
        goalId,
        fields,
      }))
    : []

  const combinedSkills = includeSkills
    ? Array.from(skillMap.entries()).map(([skillId, { fields, levelId }]) => ({
        skillId,
        fields,
        levelId,
      }))
    : []

  const combinedCultures = includeCultures
    ? Array.from(cultureMap.entries()).map(([habitId, { fields }]) => ({
        habitId,
        fields,
      }))
    : []

  return { combinedGoals, combinedSkills, combinedCultures }
}

export const getAvgGoalEvaluations = (
  evaluation: IEvaluationPure | IEvaluationState
) => {
  // Goals
  const evaluatedGoals =
    evaluation?.assessment
      ?.filter(
        (as) =>
          as.status !== SingleEvaluationStatus.NOT_COMPLETE_IN_TIME &&
          as.goalsSubmitted
      )
      .flatMap((assessment) => assessment.goals) || []

  // Create a Map to store goals grouped by `goalId`
  const goalMap = new Map<string, { totalStatus: number; count: number }>()

  // Group goals by `goalId` and calculate total and count for `goalStatus`
  evaluatedGoals.forEach((goal) => {
    const current = goalMap.get(goal.goalId) || {
      totalStatus: 0,
      count: 0,
    }

    // if suspended give value 1 as notAchieved
    current.totalStatus +=
      goal.fields.goalStatus === 4 ? 1 : goal.fields.goalStatus
    current.count += 1

    goalMap.set(goal.goalId, current)
  })

  // Calculate average and round to 1, 3 or 5
  const uniqueRoundedGoals = Array.from(goalMap.entries()).map(
    ([goalId, { totalStatus, count }]) => {
      const averageStatus = totalStatus / count
      return {
        goalId,
        goalStatus: averageStatus <= 2 ? 1 : averageStatus <= 4 ? 3 : 5,
      }
    }
  )

  return uniqueRoundedGoals
}

export const calculateAverageExpectationValues = (
  evaluations: ISingleEvaluationPure[]
): Map<string, number> => {
  const skillMap = new Map<
    string,
    {
      total: number
      count: number
    }
  >()

  evaluations.forEach((evaluation) => {
    evaluation.skills.forEach((skill) => {
      if (skill.fields.cannotScoreNow) {
        return
      }

      const existing = skillMap.get(skill.skillId)

      if (existing) {
        existing.total += skill.fields.expectationValue
        existing.count += 1
      } else {
        skillMap.set(skill.skillId, {
          total: skill.fields.expectationValue,
          count: 1,
        })
      }
    })
  })

  const averageMap = new Map<string, number>()
  skillMap.forEach((value, skillId) => {
    averageMap.set(skillId, value.total / value.count)
  })

  return averageMap
}

export const getClosestStep = (
  items: Array<{ value: number; label: string }>,
  avgr: number
) => {
  if (avgr === -1) {
    return { value: -1, label: '' }
  }
  const avg = Math.round(avgr)
  return items.reduce(
    (prev, curr) => {
      return Math.abs(curr.value - avg) < Math.abs(prev.value - avg)
        ? curr
        : prev
    },
    { value: -1, label: '' }
  )
}

export const calculatePercentageAlignmentValue = (
  evaluations: ISingleEvaluationPure[]
): Map<string, number> => {
  const cultureMap = new Map<
    string,
    {
      total: number
      count: number
    }
  >()

  evaluations.forEach((evaluation) => {
    evaluation.culture.forEach((c) => {
      const existing = cultureMap.get(c.habitId)

      if (existing) {
        existing.total += c.alignmentValue
        existing.count += 1
      } else {
        cultureMap.set(c.habitId, {
          total: c.alignmentValue,
          count: 1,
        })
      }
    })
  })

  const averageMap = new Map<string, number>()

  cultureMap.forEach((value, valueId) => {
    averageMap.set(valueId, value.total / value.count)
  })

  return averageMap
}

export const findOverlappingEntities = <
  T extends { goalId?: string; skillId?: string; habitId?: string }
>(
  entityArrays: T[][],
  idKey: keyof T
) => {
  const nonEmptyArrays = entityArrays.filter((arr) => arr.length > 0)

  if (nonEmptyArrays.length === 0) {
    return []
  }

  return nonEmptyArrays.reduce((common, entities) =>
    common.filter((item) => entities.some((e) => e[idKey] === item[idKey]))
  )
}

export const mergeUniqueAssessors = (assessors: IEvaluationAssessor[]) => {
  const assessorMap = new Map<string, IEvaluationAssessor>()

  assessors.forEach((assessor) => {
    const existing = assessorMap.get(assessor.memberId)

    if (existing) {
      // Ensure goals and skills remain true if any occurrence has true
      existing.goals = existing.goals || assessor.goals
      existing.skills = existing.skills || assessor.skills
      existing.culture = existing.culture || assessor.culture
    } else {
      assessorMap.set(assessor.memberId, { ...assessor })
    }
  })

  return Array.from(assessorMap.values())
}

export interface IMemberTermScores {
  /** Average skill score (0–100); undefined when nothing was scored. */
  skillAvg?: number
  /** Average culture alignment (0–100); undefined when nothing was scored. */
  cultureAvg?: number
  goalsTotal: number
  goalsAchieved: number
  hasSkillAssessors: boolean
  hasCultureAssessors: boolean
  hasGoalAssessors: boolean
}

// One member's skill / culture / goal result for a single term. Scored against the
// company version the evaluation was made on (so a since-renamed or retired skill
// still counts as it did then) and filtered to the contributors that member is
// allowed to see. Single source of these numbers for the results tab's KPI strip
// and the term-results PDF, so the export can't drift from the screen.
export const getMemberTermScores = (
  member: ICompanyMember,
  evaluationState: ITermStateMember,
  termState: ITermState | undefined,
  company: ICompany
): IMemberTermScores => {
  const { evaluation } = evaluationState
  const companyVersion = termState?.companyVersions?.find(
    (c) => c.__v === evaluationState.version
  )
  const versionCompany = companyVersion
    ? adaptBackendCompanyToFrontendCompany(companyVersion)
    : company

  if (!versionCompany.settings || !versionCompany.settings.evaluations) {
    versionCompany.settings = company.settings
  }

  const memberInVersion =
    versionCompany.members.find((m) => m._id === member._id) || member
  const allowed = getAllowedContributors(versionCompany, memberInVersion)
  const assessments = (evaluation.assessment || []).filter((a) =>
    allowed.some((s) => s._id === a.senderId)
  )

  const average = (map: Map<string, number>): number | undefined => {
    if (map.size === 0) return undefined
    let total = 0
    map.forEach((value) => (total += value))
    return total / map.size
  }

  const goalAvg = getAvgGoalEvaluations(evaluation)

  return {
    skillAvg: average(
      calculateAverageExpectationValues(
        assessments.filter((a) => a.skillsSubmitted)
      )
    ),
    cultureAvg: average(
      calculatePercentageAlignmentValue(
        assessments.filter((a) => a.cultureSubmitted)
      )
    ),
    goalsTotal: goalAvg.length,
    // goalStatus 3 is the "achieved" step (see getAvgGoalEvaluations).
    goalsAchieved: goalAvg.filter((goal) => goal.goalStatus === 3).length,
    hasSkillAssessors: evaluation.assessors.some((a) => a.skills),
    hasCultureAssessors: evaluation.assessors.some((a) => a.culture),
    hasGoalAssessors: evaluation.assessors.some((a) => a.goals),
  }
}
